import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { ArrowLeft, Info, Plus, Save, Trash2 } from 'lucide-react';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormSection } from '@components/form-section';
import { PageHeader } from '@components/page-header';
import { Select } from '@components/select';
import { Spinner } from '@components/spinner';
import { StickyBar } from '@components/sticky-bar';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@components/ui/tooltip';
import { Api } from '@core/api';
import {
    AggregationAlgorithm,
    ChallengeLeaderboardType,
    ChallengePageEntrySupportDto,
    CommunityLeaderboardSettingsUpdateRequest,
    I18NText,
    LeaderboardActivityCategoryFilter,
    LeaderboardDateFilter,
    LeaderboardDateFilterType,
    LeaderboardGenderMode,
    LeaderboardMetric,
    WorkoutCategory,
    WorkoutTypeV2
} from '@core/api/ChallengeClient';
import {
    CompositePointDefinitionDto,
    WorkoutCategoryWithTypesDto
} from '@core/api/FitnessActivityClient';
import { links } from '@core/router';
import { injectTSDI } from '@core/tsdi';
import { useCommunitySettingsStore } from '@src/communities/community/details/store';
import { getDragId } from './constants';
import {
    ChallengeLeaderboardTypeSelector,
    ChallengeSelector,
    CompositePointKeySelector,
    CustomDateRange,
    DateFilterTypeSelector,
    GenderModeSelector,
    MetricSelector,
    RewardCurrencyInput,
    WorkoutCategorySelector,
    WorkoutTypeSelector
} from './filter-field-components';
import { getAutoConfiguredFields, getChallengeFilterError } from './helpers';
import { LabelToggle } from './label-toggle';
import { SortableItem } from './sortable-components';
import { StyledLocalizedTextField } from './styled-localized-textfield';
import { VisibilityToggle } from './visibility-toggle';

const aggregationOptions = [
    { label: 'Average', value: AggregationAlgorithm.AVERAGE },
    { label: 'Sum', value: AggregationAlgorithm.SUM }
];

const RemoveButton = ({ onClick }: { onClick(): void }) => (
    <Button
        variant="ghost"
        size="icon-sm"
        className="text-destructive"
        aria-label="Remove filter"
        onClick={onClick}
    >
        <Trash2 />
    </Button>
);

const EditCommunityLeaderboardSettingsForm = observer(() => {
    const { communityId } = useParams();
    const navigate = useNavigate();

    const {
        leaderboardSettings,
        updateLeaderboardSettings,
        handleUpdateLeaderboardSettings
    } = useCommunitySettingsStore();
    const { fitnessActivityClient, challengeClient } = injectTSDI(Api);

    const loading = leaderboardSettings.loading;
    const saving = updateLeaderboardSettings.loading;
    const error =
        leaderboardSettings.error?.message ||
        updateLeaderboardSettings.error?.message ||
        null;

    const [dateFilters, setDateFilters] = useState<LeaderboardDateFilter[]>([]);
    const [activityCategoryFilters, setActivityCategoryFilters] = useState<
        LeaderboardActivityCategoryFilter[]
    >([]);
    const [workoutTypesByCategory, setWorkoutTypesByCategory] = useState<
        Partial<Record<WorkoutCategory, WorkoutTypeV2[]>>
    >({});
    const [definitions, setDefinitions] = useState<
        CompositePointDefinitionDto[]
    >([]);
    const [definitionsLoading, setDefinitionsLoading] = useState(false);
    const [challenges, setChallenges] = useState<
        ChallengePageEntrySupportDto[]
    >([]);
    const [challengesLoading, setChallengesLoading] = useState(false);
    const [pointsAggregationType, setPointsAggregationType] =
        useState<AggregationAlgorithm>(AggregationAlgorithm.AVERAGE);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const handleDateFilterDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = dateFilters.findIndex(
            (_, i) => getDragId.dateFilter(i) === active.id
        );
        const newIndex = dateFilters.findIndex(
            (_, i) => getDragId.dateFilter(i) === over.id
        );
        setDateFilters(arrayMove(dateFilters, oldIndex, newIndex));
    };

    const handleActivityFilterDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = activityCategoryFilters.findIndex(
            (_, i) => getDragId.activityFilter(i) === active.id
        );
        const newIndex = activityCategoryFilters.findIndex(
            (_, i) => getDragId.activityFilter(i) === over.id
        );
        setActivityCategoryFilters(
            arrayMove(activityCategoryFilters, oldIndex, newIndex)
        );
    };

    useEffect(() => {
        const fetchData = async () => {
            const settingsData = await leaderboardSettings.request([
                parseInt(communityId)
            ]);
            if (settingsData) {
                setDateFilters(settingsData.dateFilters || []);
                setActivityCategoryFilters(
                    settingsData.activityCategoryFilters || []
                );
                setPointsAggregationType(settingsData.pointsAggregationType);
            }
        };
        fetchData();
    }, [communityId, leaderboardSettings]);

    useEffect(() => {
        const fetchWorkoutTypes = async () => {
            try {
                const response =
                    await fitnessActivityClient.admin.workoutCategoryAdminControllerGetWorkoutCategories();
                const categories = response.data || [];
                const mapped = categories.reduce<
                    Record<WorkoutCategory, WorkoutTypeV2[]>
                >(
                    (acc, item: WorkoutCategoryWithTypesDto) => {
                        const category =
                            item.category as unknown as WorkoutCategory;
                        const types = (item.types || []).map(
                            (type) => type as unknown as WorkoutTypeV2
                        );
                        acc[category] = types;
                        return acc;
                    },
                    {} as Record<WorkoutCategory, WorkoutTypeV2[]>
                );
                setWorkoutTypesByCategory(mapped);
            } catch (e) {
                console.error('Failed to load workout categories', e);
            }
        };
        fetchWorkoutTypes();
    }, [fitnessActivityClient]);

    const hasCompositePointsFilter = activityCategoryFilters.some(
        (f) => f.metric === LeaderboardMetric.COMPOSITE_POINTS
    );

    const hasChallengeFilter = activityCategoryFilters.some(
        (f) => f.metric === LeaderboardMetric.CHALLENGE
    );

    useEffect(() => {
        if (!hasChallengeFilter || challenges.length > 0) return;
        setChallengesLoading(true);
        challengeClient.support
            .challengeSupportControllerGetPage({
                communityIds: [parseInt(communityId)],
                size: 200
            })
            .then((res) => setChallenges(res.data?.content ?? []))
            .catch(() => setChallenges([]))
            .finally(() => setChallengesLoading(false));
    }, [hasChallengeFilter]);

    useEffect(() => {
        if (!hasCompositePointsFilter || definitions.length > 0) return;
        setDefinitionsLoading(true);
        fitnessActivityClient.admin
            .compositePointDefinitionControllerGetByCommunityId({
                communityId: parseInt(communityId)
            })
            .then((res) => setDefinitions(res.data ?? []))
            .catch(() => setDefinitions([]))
            .finally(() => setDefinitionsLoading(false));
    }, [hasCompositePointsFilter]);

    const handleAddDateFilter = () => {
        setDateFilters([
            ...dateFilters,
            {
                filterType: LeaderboardDateFilterType.CUSTOM
            }
        ]);
    };

    const handleRemoveDateFilter = (index: number) => {
        setDateFilters(dateFilters.filter((_, i) => i !== index));
    };

    const handleDateFilterChange = (
        index: number,
        field: keyof LeaderboardDateFilter,
        value: any
    ) => {
        const updated = [...dateFilters];
        updated[index] = { ...updated[index], [field]: value };
        setDateFilters(updated);
    };

    const handleAddActivityCategoryFilter = () => {
        setActivityCategoryFilters([
            ...activityCategoryFilters,
            {
                metric: LeaderboardMetric.ACTIVITY_POINTS,
                genderFilter: LeaderboardGenderMode.DISABLED
            }
        ]);
    };

    const handleRemoveActivityCategoryFilter = (index: number) => {
        setActivityCategoryFilters(
            activityCategoryFilters.filter((_, i) => i !== index)
        );
    };

    const handleActivityCategoryFilterChange = (
        index: number,
        field: keyof LeaderboardActivityCategoryFilter,
        value: any
    ) => {
        const updated = [...activityCategoryFilters];

        if (field === 'metric') {
            updated[index] = getAutoConfiguredFields(
                value as LeaderboardMetric,
                updated[index]
            );
        } else if (field === 'workoutCategory') {
            updated[index] = {
                ...updated[index],
                workoutCategory: value as WorkoutCategory | undefined,
                workoutType: undefined
            };
        } else if (field === 'challengeLeaderboardType') {
            updated[index] = {
                ...updated[index],
                challengeLeaderboardType: value as
                    | ChallengeLeaderboardType
                    | undefined,
                genderFilter:
                    value === ChallengeLeaderboardType.MEMBER
                        ? updated[index].genderFilter
                        : LeaderboardGenderMode.DISABLED
            };
        } else {
            updated[index] = { ...updated[index], [field]: value };
        }

        setActivityCategoryFilters(updated);
    };

    const challengeFilterError = getChallengeFilterError(
        activityCategoryFilters
    );

    const handleSave = async () => {
        const updateData: CommunityLeaderboardSettingsUpdateRequest = {
            pointsAggregationType,
            dateFilters: dateFilters,
            activityCategoryFilters: activityCategoryFilters
        };

        await handleUpdateLeaderboardSettings(
            parseInt(communityId),
            updateData
        );

        if (!updateLeaderboardSettings.error) {
            navigate(links.communityDetailsSettings(communityId));
        }
    };

    const handleCancel = () => {
        navigate(links.communityDetailsSettings(communityId));
    };

    if (loading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (error && !dateFilters) {
        return (
            <div className="flex flex-col items-start gap-4">
                <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
                <Button
                    variant="ghost"
                    onClick={() => navigate(links.communities())}
                >
                    <ArrowLeft />
                    Back to Communities
                </Button>
            </div>
        );
    }

    return (
        <>
            <PageHeader title={`Edit Leaderboard Settings: ${communityId}`} />

            <div className="flex flex-col gap-6 pb-24">
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {challengeFilterError && (
                    <Alert variant="warning">
                        <AlertDescription>
                            {challengeFilterError}
                        </AlertDescription>
                    </Alert>
                )}

                <FormSection
                    title="Date Filters"
                    actions={
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleAddDateFilter}
                        >
                            <Plus />
                            Add Date Filter
                        </Button>
                    }
                >
                    {dateFilters.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            No date filters configured. Click "Add Date Filter"
                            to add one.
                        </p>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDateFilterDragEnd}
                        >
                            <SortableContext
                                items={dateFilters.map((_, i) =>
                                    getDragId.dateFilter(i)
                                )}
                                strategy={verticalListSortingStrategy}
                            >
                                <div className="flex flex-col gap-2">
                                    {dateFilters.map((filter, index) => (
                                        <SortableItem
                                            key={getDragId.dateFilter(index)}
                                            id={getDragId.dateFilter(index)}
                                        >
                                            <DateFilterTypeSelector
                                                value={filter.filterType}
                                                onChange={(value) =>
                                                    handleDateFilterChange(
                                                        index,
                                                        'filterType',
                                                        value
                                                    )
                                                }
                                            />

                                            {filter.filterType ===
                                                LeaderboardDateFilterType.CUSTOM && (
                                                <>
                                                    <CustomDateRange
                                                        dateFrom={
                                                            filter.dateFrom
                                                        }
                                                        dateTo={filter.dateTo}
                                                        onDateFromChange={(
                                                            value
                                                        ) =>
                                                            handleDateFilterChange(
                                                                index,
                                                                'dateFrom',
                                                                value
                                                            )
                                                        }
                                                        onDateToChange={(
                                                            value
                                                        ) =>
                                                            handleDateFilterChange(
                                                                index,
                                                                'dateTo',
                                                                value
                                                            )
                                                        }
                                                    />
                                                    <StyledLocalizedTextField
                                                        label="Label"
                                                        value={
                                                            filter.label || {}
                                                        }
                                                        onChange={(
                                                            value: I18NText
                                                        ) =>
                                                            handleDateFilterChange(
                                                                index,
                                                                'label',
                                                                value
                                                            )
                                                        }
                                                    />
                                                </>
                                            )}

                                            <VisibilityToggle
                                                validFrom={filter.validFrom}
                                                validTo={filter.validTo}
                                                onValidFromChange={(value) =>
                                                    handleDateFilterChange(
                                                        index,
                                                        'validFrom',
                                                        value
                                                    )
                                                }
                                                onValidToChange={(value) =>
                                                    handleDateFilterChange(
                                                        index,
                                                        'validTo',
                                                        value
                                                    )
                                                }
                                            />

                                            <RemoveButton
                                                onClick={() =>
                                                    handleRemoveDateFilter(
                                                        index
                                                    )
                                                }
                                            />
                                        </SortableItem>
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    )}
                </FormSection>

                <FormSection
                    title="Activity Category Filters"
                    actions={
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleAddActivityCategoryFilter}
                        >
                            <Plus />
                            Add Activity Filter
                        </Button>
                    }
                >
                    {activityCategoryFilters.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            No activity category filters configured. Click "Add
                            Activity Filter" to add one.
                        </p>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleActivityFilterDragEnd}
                        >
                            <SortableContext
                                items={activityCategoryFilters.map((_, i) =>
                                    getDragId.activityFilter(i)
                                )}
                                strategy={verticalListSortingStrategy}
                            >
                                <div className="flex flex-col gap-2">
                                    {activityCategoryFilters.map(
                                        (filter, index) => (
                                            <SortableItem
                                                key={getDragId.activityFilter(
                                                    index
                                                )}
                                                id={getDragId.activityFilter(
                                                    index
                                                )}
                                            >
                                                <MetricSelector
                                                    value={filter.metric}
                                                    onChange={(value) =>
                                                        handleActivityCategoryFilterChange(
                                                            index,
                                                            'metric',
                                                            value
                                                        )
                                                    }
                                                />

                                                {(filter.metric ===
                                                    LeaderboardMetric.DISTANCE ||
                                                    filter.metric ===
                                                        LeaderboardMetric.TIME) && (
                                                    <WorkoutCategorySelector
                                                        value={
                                                            filter.workoutCategory
                                                        }
                                                        onChange={(value) =>
                                                            handleActivityCategoryFilterChange(
                                                                index,
                                                                'workoutCategory',
                                                                value
                                                            )
                                                        }
                                                        metric={filter.metric}
                                                    />
                                                )}

                                                {filter.workoutCategory && (
                                                    <WorkoutTypeSelector
                                                        value={
                                                            filter.workoutType
                                                        }
                                                        onChange={(value) =>
                                                            handleActivityCategoryFilterChange(
                                                                index,
                                                                'workoutType',
                                                                value
                                                            )
                                                        }
                                                        options={
                                                            workoutTypesByCategory[
                                                                filter
                                                                    .workoutCategory
                                                            ] || []
                                                        }
                                                    />
                                                )}

                                                {filter.metric ===
                                                    LeaderboardMetric.REWARDS && (
                                                    <RewardCurrencyInput
                                                        value={
                                                            filter.rewardCurrencyCode
                                                        }
                                                        onChange={(value) =>
                                                            handleActivityCategoryFilterChange(
                                                                index,
                                                                'rewardCurrencyCode',
                                                                value
                                                            )
                                                        }
                                                    />
                                                )}

                                                {filter.metric ===
                                                    LeaderboardMetric.COMPOSITE_POINTS && (
                                                    <CompositePointKeySelector
                                                        value={
                                                            filter.pointTypeKey
                                                        }
                                                        onChange={(value) =>
                                                            handleActivityCategoryFilterChange(
                                                                index,
                                                                'pointTypeKey',
                                                                value
                                                            )
                                                        }
                                                        definitions={
                                                            definitions
                                                        }
                                                        loading={
                                                            definitionsLoading
                                                        }
                                                    />
                                                )}

                                                {filter.metric ===
                                                    LeaderboardMetric.CHALLENGE && (
                                                    <>
                                                        <ChallengeSelector
                                                            value={
                                                                filter.challengeId
                                                            }
                                                            onChange={(value) =>
                                                                handleActivityCategoryFilterChange(
                                                                    index,
                                                                    'challengeId',
                                                                    value
                                                                )
                                                            }
                                                            challenges={
                                                                challenges
                                                            }
                                                            loading={
                                                                challengesLoading
                                                            }
                                                        />

                                                        <ChallengeLeaderboardTypeSelector
                                                            value={
                                                                filter.challengeLeaderboardType
                                                            }
                                                            onChange={(value) =>
                                                                handleActivityCategoryFilterChange(
                                                                    index,
                                                                    'challengeLeaderboardType',
                                                                    value
                                                                )
                                                            }
                                                        />

                                                        <StyledLocalizedTextField
                                                            label="Label"
                                                            required
                                                            value={
                                                                filter.label ||
                                                                {}
                                                            }
                                                            onChange={(
                                                                value: I18NText
                                                            ) =>
                                                                handleActivityCategoryFilterChange(
                                                                    index,
                                                                    'label',
                                                                    value
                                                                )
                                                            }
                                                        />
                                                    </>
                                                )}

                                                {(filter.metric !==
                                                    LeaderboardMetric.CHALLENGE ||
                                                    filter.challengeLeaderboardType ===
                                                        ChallengeLeaderboardType.MEMBER) && (
                                                    <GenderModeSelector
                                                        value={
                                                            filter.genderFilter
                                                        }
                                                        onChange={(value) =>
                                                            handleActivityCategoryFilterChange(
                                                                index,
                                                                'genderFilter',
                                                                value
                                                            )
                                                        }
                                                    />
                                                )}

                                                {filter.metric !==
                                                    LeaderboardMetric.CHALLENGE && (
                                                    <LabelToggle
                                                        label={filter.label}
                                                        onLabelChange={(
                                                            value
                                                        ) =>
                                                            handleActivityCategoryFilterChange(
                                                                index,
                                                                'label',
                                                                value
                                                            )
                                                        }
                                                    />
                                                )}

                                                <VisibilityToggle
                                                    validFrom={filter.validFrom}
                                                    validTo={filter.validTo}
                                                    onValidFromChange={(
                                                        value
                                                    ) =>
                                                        handleActivityCategoryFilterChange(
                                                            index,
                                                            'validFrom',
                                                            value
                                                        )
                                                    }
                                                    onValidToChange={(value) =>
                                                        handleActivityCategoryFilterChange(
                                                            index,
                                                            'validTo',
                                                            value
                                                        )
                                                    }
                                                />

                                                <RemoveButton
                                                    onClick={() =>
                                                        handleRemoveActivityCategoryFilter(
                                                            index
                                                        )
                                                    }
                                                />
                                            </SortableItem>
                                        )
                                    )}
                                </div>
                            </SortableContext>
                        </DndContext>
                    )}
                </FormSection>

                <FormSection
                    title="Team Leaderboard"
                    actions={
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Info className="size-4 cursor-help text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[340px] p-3">
                                <p className="mb-1 font-bold">
                                    Team Leaderboard
                                </p>
                                <p className="font-semibold">
                                    Points Aggregation
                                </p>
                                <p className="mb-1">
                                    How a team score is calculated from member
                                    points on the Teams leaderboard:
                                </p>
                                <ul className="list-disc pl-4">
                                    <li>Average — mean of member points</li>
                                    <li>Sum — total of member points</li>
                                    <li>
                                        Does not affect individual leaderboards
                                    </li>
                                </ul>
                            </TooltipContent>
                        </Tooltip>
                    }
                >
                    <div className="w-[200px]">
                        <Select
                            label="Points Aggregation"
                            value={pointsAggregationType}
                            options={aggregationOptions}
                            onChange={setPointsAggregationType}
                        />
                    </div>
                </FormSection>
            </div>

            <StickyBar>
                <Button
                    variant="ghost"
                    onClick={handleCancel}
                    disabled={saving}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    loading={saving}
                    disabled={challengeFilterError !== null}
                >
                    <Save />
                    Save
                </Button>
            </StickyBar>
        </>
    );
});

export default EditCommunityLeaderboardSettingsForm;
